import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Snackbar,
  Alert,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import UploadIcon from '@mui/icons-material/Upload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Breadcrumbs from '../../components/layout/Breadcrumbs';
import ReturnEntryForm from '../../components/marketplace/ReturnEntryForm';
import ReturnEntryList from '../../components/marketplace/ReturnEntryList';
import { getReturnEntryLogs, createReturnEntryLog, deleteReturnEntryLog, downloadReturnEntryLogsExcel, uploadReturnEntryLogsExcel, checkAwbAvailability } from '../../services/marketplaceapi';

const ReturnEntryLogPage = () => {
  // State for table data
  const [returnLogs, setReturnLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Search/Filter state
  const [filters, setFilters] = useState({
    marketplace: '',
    company: '',
    awb: '',
    start_date: null,
    end_date: null
  });

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Download and upload states
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [checkAwbLoading, setCheckAwbLoading] = useState(false);
  
  // File upload states
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedAwbFile, setSelectedAwbFile] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [checkAwbDialogOpen, setCheckAwbDialogOpen] = useState(false);
  
  // File input refs
  const fileInputRef = useRef(null);
  const awbFileInputRef = useRef(null);

  // Marketplace and company options
  const marketplaceOptions = ['Amazon', 'Flipkart', 'Myntra', 'Ajio'];
  const companyOptions = ['ADAC Construction', 'Pujoy Fashion', 'Vitti'];

  // Fetch data on component mount and whenever page or filters change
  useEffect(() => {
    fetchReturnLogs();
  }, [page, filters]);

  const fetchReturnLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      // Prepare params for API call
      const params = {
        page,
        page_size: rowsPerPage,
        _t: new Date().getTime() // Cache buster
      };

      // Add filters to params if they have values
      if (filters.marketplace) params.marketplace = filters.marketplace;
      if (filters.company) params.company = filters.company;
      if (filters.awb) params.awb = filters.awb;
      if (filters.start_date) params.start_date = filters.start_date.toISOString().split('T')[0];
      if (filters.end_date) params.end_date = filters.end_date.toISOString().split('T')[0];

      const response = await getReturnEntryLogs(params);
      
      if (response.data.results) {
        setReturnLogs(response.data.results);
        setTotalCount(response.data.count);
      } else {
        // Fallback for non-paginated response
        setReturnLogs(response.data);
        setTotalCount(response.data.length);
      }
    } catch (err) {
      console.error('Error fetching return logs:', err);
      setError('Failed to load return logs. Please try again.');
      setReturnLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      setLoading(true);
      
      // Validate form data
      if (!formData.marketplace || !formData.company || !formData.AWB) {
        setNotification({
          open: true,
          message: 'Please fill all required fields',
          severity: 'error'
        });
        setLoading(false);
        return;
      }
      
      // Submit the data
      await createReturnEntryLog(formData);
      
      // Show success notification
      setNotification({
        open: true,
        message: 'Return entry log added successfully',
        severity: 'success'
      });
      
      // Refresh data
      fetchReturnLogs();
    } catch (err) {
      console.error('Error creating return log:', err);
      setNotification({
        open: true,
        message: `Failed to add return log: ${err.response?.data?.detail || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this return entry?')) {
      return;
    }
    
    try {
      setLoading(true);
      await deleteReturnEntryLog(id);
      
      // Show success notification
      setNotification({
        open: true,
        message: 'Return entry log deleted successfully',
        severity: 'success'
      });
      
      // Refresh data
      fetchReturnLogs();
    } catch (err) {
      console.error('Error deleting return log:', err);
      setNotification({
        open: true,
        message: `Failed to delete return log: ${err.response?.data?.detail || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setPage(1); // Reset to first page when filtering
  };

  const handleClearFilters = () => {
    setFilters({
      marketplace: '',
      company: '',
      awb: '',
      start_date: null,
      end_date: null
    });
    setPage(1);
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloadLoading(true);
      
      const response = await downloadReturnEntryLogsExcel();
      
      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'return_entry_logs.xlsx';
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches) filename = matches[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setNotification({
        open: true,
        message: 'Excel file downloaded successfully',
        severity: 'success'
      });
      
    } catch (err) {
      console.error('Error downloading Excel file:', err);
      setNotification({
        open: true,
        message: `Failed to download Excel file: ${err.response?.data?.error || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleNotificationClose = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Handle file selection for bulk upload
  const handleFileChange = (event) => {
    if (event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
  };

  // Handle file selection for AWB checking
  const handleAwbFileChange = (event) => {
    if (event.target.files.length > 0) {
      setSelectedAwbFile(event.target.files[0]);
    }
  };

  // Open the upload dialog
  const handleOpenUploadDialog = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setUploadDialogOpen(true);
  };

  // Close the upload dialog
  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
  };

  // Open the check AWB dialog
  const handleOpenCheckAwbDialog = () => {
    setSelectedAwbFile(null);
    if (awbFileInputRef.current) {
      awbFileInputRef.current.value = '';
    }
    setCheckAwbDialogOpen(true);
  };

  // Close the check AWB dialog
  const handleCloseCheckAwbDialog = () => {
    setCheckAwbDialogOpen(false);
  };

  // Handle upload of Excel file for bulk creating/updating return entry logs
  const handleUploadExcel = async () => {
    if (!selectedFile) {
      setNotification({
        open: true,
        message: 'Please select a file first',
        severity: 'error'
      });
      return;
    }
    
    try {
      setUploadLoading(true);
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await uploadReturnEntryLogsExcel(formData);
      
      setNotification({
        open: true,
        message: `Upload successful! Created: ${response.data.created}, Updated: ${response.data.updated}`,
        severity: 'success'
      });
      
      // Close dialog and refresh data
      handleCloseUploadDialog();
      fetchReturnLogs();
      
    } catch (err) {
      console.error('Error uploading Excel file:', err);
      setNotification({
        open: true,
        message: `Upload failed: ${err.response?.data?.error || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setUploadLoading(false);
    }
  };

  // Handle upload of Excel file for checking AWB availability
  const handleCheckAwbAvailability = async () => {
    if (!selectedAwbFile) {
      setNotification({
        open: true,
        message: 'Please select a file first',
        severity: 'error'
      });
      return;
    }
    
    try {
      setCheckAwbLoading(true);
      
      const formData = new FormData();
      formData.append('file', selectedAwbFile);
      
      const response = await checkAwbAvailability(formData);
      
      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'awb_availability.xlsx';
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches) filename = matches[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setNotification({
        open: true,
        message: 'AWB availability check completed and file downloaded',
        severity: 'success'
      });
      
      // Close dialog
      handleCloseCheckAwbDialog();
      
    } catch (err) {
      console.error('Error checking AWB availability:', err);
      setNotification({
        open: true,
        message: `AWB check failed: ${err.response?.data?.error || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setCheckAwbLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
        <Breadcrumbs items={[
          { label: 'Home', path: '/dashboard' },
          { label: 'Marketplace', path: '/marketplace/amazon' },
          { label: 'Return Entry Log', path: '/marketplace/return-entry-log' }
        ]} />

        {/* Return Entry Form */}
        <ReturnEntryForm 
          onSubmit={handleFormSubmit}
          loading={loading}
          marketplaceOptions={marketplaceOptions}
          companyOptions={companyOptions}
        />

        {/* Search and Filter Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Search & Filter
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Marketplace</InputLabel>
                <Select
                  sx={{ width: 150 }}
                  value={filters.marketplace}
                  label="Marketplace"
                  onChange={(e) => handleFilterChange('marketplace', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {marketplaceOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Company</InputLabel>
                <Select
                  sx={{ width: 150 }}
                  value={filters.company}
                  label="Company"
                  onChange={(e) => handleFilterChange('company', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {companyOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="AWB"
                value={filters.awb}
                onChange={(e) => handleFilterChange('awb', e.target.value)}
                placeholder="Search by AWB"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="Start Date"
                value={filters.start_date}
                onChange={(date) => handleFilterChange('start_date', date)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="End Date"
                value={filters.end_date}
                onChange={(date) => handleFilterChange('end_date', date)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                fullWidth
                size="small"
              >
                Clear
              </Button>
            </Grid>
          </Grid>
          
          {/* Action Buttons */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Tooltip title="Upload Excel file to bulk create/update return entries">
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={handleOpenUploadDialog}
                color="primary"
              >
                Upload Excel
              </Button>
            </Tooltip>
            
            <Tooltip title="Check if AWBs exist in the system">
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={handleOpenCheckAwbDialog}
                color="info"
              >
                Check AWB Availability
              </Button>
            </Tooltip>
            
            <Tooltip title="Download all return entries as Excel file">
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadExcel}
                disabled={downloadLoading}
                color="success"
              >
                {downloadLoading ? 'Downloading...' : 'Download Excel'}
              </Button>
            </Tooltip>
          </Box>
        </Paper>

        {/* Return Entries List */}
        <ReturnEntryList 
          returnLogs={returnLogs}
          loading={loading}
          error={error}
          onRefresh={fetchReturnLogs}
          onDelete={handleDelete}
          page={page}
          onPageChange={handlePageChange}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
        />

        {/* Notifications */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleNotificationClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleNotificationClose} 
            severity={notification.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
        
        {/* Upload Excel Dialog */}
        <Dialog
          open={uploadDialogOpen}
          onClose={handleCloseUploadDialog}
          aria-labelledby="upload-dialog-title"
        >
          <DialogTitle id="upload-dialog-title">Upload Return Entry Excel</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Upload an Excel file with return entry data to bulk create or update records.
              <br /><br />
              The Excel file should have the following columns:
              <ul>
                <li>Date - Format: YYYY-MM-DD HH:MM:SS</li>
                <li>Market Place - One of: Amazon, Flipkart, Myntra, Ajio</li>
                <li>Company - The company name</li>
                <li>AWB No - The AWB number</li>
              </ul>
            </DialogContentText>
            <Box sx={{ mt: 2 }}>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                ref={fileInputRef}
                style={{ display: 'block', marginTop: '16px' }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUploadDialog} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={handleUploadExcel} 
              color="primary" 
              disabled={!selectedFile || uploadLoading}
              variant="contained"
            >
              {uploadLoading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Check AWB Availability Dialog */}
        <Dialog
          open={checkAwbDialogOpen}
          onClose={handleCloseCheckAwbDialog}
          aria-labelledby="check-awb-dialog-title"
        >
          <DialogTitle id="check-awb-dialog-title">Check AWB Availability</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Upload an Excel file with AWB numbers to check if they exist in the system.
              <br /><br />
              The Excel file should have a column named "AWB" containing the AWB numbers to check.
              <br /><br />
              You will receive an Excel file with an additional "Available" column indicating "Yes" or "No" for each AWB.
            </DialogContentText>
            <Box sx={{ mt: 2 }}>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleAwbFileChange}
                ref={awbFileInputRef}
                style={{ display: 'block', marginTop: '16px' }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCheckAwbDialog} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={handleCheckAwbAvailability} 
              color="primary" 
              disabled={!selectedAwbFile || checkAwbLoading}
              variant="contained"
            >
              {checkAwbLoading ? 'Processing...' : 'Check Availability'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ReturnEntryLogPage;
